import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./SuperAdminPage.css";

type OrganizationRow = {
  organization_id: string;
  name: string;
  legal_name: string | null;
  tax_id: string | null;
  status: "active" | "inactive" | "archived";
  member_count: number | string;
  customer_count: number | string;
  enabled_module_count: number | string;
  created_at: string;
  updated_at: string;
};

export function SuperAdminPage() {
  const {
    selectedOrganization,
    refreshOrganizations,
  } = useOrganization();

  const isPlatformAdmin = Boolean(selectedOrganization?.isPlatformAdmin);

  const [organizations, setOrganizations] = useState<OrganizationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const [name, setName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [initialAdminEmail, setInitialAdminEmail] = useState("");
  const [initialRole, setInitialRole] = useState<"owner" | "admin">("owner");

  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!isPlatformAdmin) {
      setOrganizations([]);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "list_platform_organizations",
    );

    if (rpcError) {
      setOrganizations([]);
      setError(rpcError.message);
      setLoading(false);
      return;
    }

    setOrganizations((data ?? []) as OrganizationRow[]);
    setLoading(false);
  }, [isPlatformAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeCount = useMemo(
    () => organizations.filter((item) => item.status === "active").length,
    [organizations],
  );

  const totalMembers = useMemo(
    () =>
      organizations.reduce(
        (sum, item) => sum + Number(item.member_count || 0),
        0,
      ),
    [organizations],
  );

  const totalCustomers = useMemo(
    () =>
      organizations.reduce(
        (sum, item) => sum + Number(item.customer_count || 0),
        0,
      ),
    [organizations],
  );

  async function createOrganization(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setCreating(true);
    setError(null);
    setMessage(null);

    const { data: organizationId, error: rpcError } = await supabase.rpc(
      "create_platform_organization",
      {
        p_name: name,
        p_legal_name: legalName.trim() || null,
        p_tax_id: taxId.trim() || null,
        p_email: email.trim() || null,
        p_phone: phone.trim() || null,
        p_address: address.trim() || null,
      },
    );

    if (rpcError || !organizationId) {
      setCreating(false);
      setError(rpcError?.message ?? "No se pudo crear la empresa.");
      return;
    }

    const normalizedAdminEmail = initialAdminEmail.trim().toLowerCase();
    let invitationMessage = "";

    if (normalizedAdminEmail) {
      const { data: invitationData, error: invitationError } =
        await supabase.functions.invoke("invite-company-user", {
          body: {
            organization_id: organizationId,
            email: normalizedAdminEmail,
            role: initialRole,
            send_email: true,
          },
        });

      const invitationResult = invitationData as {
        ok?: boolean;
        email_sent?: boolean;
        email_state?: string;
        error?: string;
      } | null;

      if (invitationError || !invitationResult?.ok) {
        invitationMessage =
          " Empresa creada, pero la invitación del administrador no pudo enviarse: " +
          (invitationResult?.error ??
            invitationError?.message ??
            "error desconocido") +
          ".";
      } else if (invitationResult.email_sent) {
        invitationMessage =
          initialRole === "owner"
            ? " Invitación enviada al propietario inicial."
            : " Invitación enviada al primer administrador.";
      } else {
        invitationMessage =
          " Invitación inicial creada; estado de correo: " +
          (invitationResult.email_state ?? "no enviado") +
          ".";
      }
    }

    setCreating(false);
    setName("");
    setLegalName("");
    setTaxId("");
    setEmail("");
    setPhone("");
    setAddress("");
    setInitialAdminEmail("");
    setInitialRole("owner");
    setFormOpen(false);
    setMessage("Empresa creada correctamente." + invitationMessage);

    await Promise.all([load(), refreshOrganizations()]);
  }

  async function toggleStatus(organization: OrganizationRow) {
    const nextStatus =
      organization.status === "active" ? "inactive" : "active";

    setBusyId(organization.organization_id);
    setError(null);
    setMessage(null);

    const { error: rpcError } = await supabase.rpc(
      "set_platform_organization_status",
      {
        p_organization_id: organization.organization_id,
        p_status: nextStatus,
      },
    );

    setBusyId(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setMessage(
      nextStatus === "active"
        ? `${organization.name} activada.`
        : `${organization.name} desactivada.`,
    );

    await Promise.all([load(), refreshOrganizations()]);
  }

  if (!isPlatformAdmin) {
    return (
      <div className="page">
        <section className="empty-state empty-state-error">
          <strong>Acceso restringido.</strong>
          <p>Esta sección requiere permisos de Super Admin.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Super Admin</span>
          <h1>Administración de plataforma</h1>
          <p>Empresas, acceso global y estado operativo del Core SaaS.</p>
        </div>

        <button
          className="primary-button"
          type="button"
          onClick={() => setFormOpen((value) => !value)}
        >
          Nueva empresa
        </button>
      </header>

      <section className="superadmin-summary-grid">
        <div className="metric-card">
          <span>Empresas</span>
          <strong>{loading ? "…" : organizations.length}</strong>
        </div>
        <div className="metric-card">
          <span>Activas</span>
          <strong>{loading ? "…" : activeCount}</strong>
        </div>
        <div className="metric-card">
          <span>Usuarios activos</span>
          <strong>{loading ? "…" : totalMembers}</strong>
        </div>
        <div className="metric-card">
          <span>Clientes</span>
          <strong>{loading ? "…" : totalCustomers}</strong>
        </div>
      </section>

      {formOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Alta</span>
              <h2>Nueva empresa</h2>
            </div>
          </div>

          <form className="superadmin-form" onSubmit={createOrganization}>
            <label>
              Nombre comercial
              <input
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label>
              Razón social
              <input
                value={legalName}
                onChange={(event) => setLegalName(event.target.value)}
              />
            </label>
            <label>
              RUC / identificación fiscal
              <input
                value={taxId}
                onChange={(event) => setTaxId(event.target.value)}
              />
            </label>
            <label>
              Correo
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label>
              Teléfono
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </label>
            <label className="field-span-full">
              Dirección
              <input
                value={address}
                onChange={(event) => setAddress(event.target.value)}
              />
            </label>

            <label>
              Responsable inicial
              <input
                type="email"
                value={initialAdminEmail}
                onChange={(event) =>
                  setInitialAdminEmail(event.target.value)
                }
                placeholder="persona@empresa.com (opcional)"
              />
            </label>

            <label>
              Rol inicial
              <select
                value={initialRole}
                onChange={(event) =>
                  setInitialRole(event.target.value as "owner" | "admin")
                }
                disabled={!initialAdminEmail.trim()}
              >
                <option value="owner">Propietario</option>
                <option value="admin">Administrador</option>
              </select>
              <small>
                Propietario se permite solo en empresas sin propietario activo.
              </small>
            </label>

            <div className="form-actions field-span-full">
              <button
                className="primary-button"
                type="submit"
                disabled={creating}
              >
                {creating ? "Creando…" : "Crear empresa"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {error ? <div className="inline-alert">{error}</div> : null}
      {message ? <div className="form-success superadmin-message">{message}</div> : null}

      <section className="superadmin-grid">
        {organizations.map((organization) => (
          <article
            className="superadmin-card"
            key={organization.organization_id}
          >
            <div className="superadmin-card-heading">
              <div>
                <span
                  className={
                    organization.status === "active"
                      ? "status-badge status-badge-approved"
                      : "status-badge"
                  }
                >
                  {organization.status}
                </span>
                <h2>{organization.name}</h2>
                <p>{organization.legal_name ?? "Sin razón social"}</p>
              </div>
              <button
                className={
                  organization.status === "active"
                    ? "compact-button compact-button-danger"
                    : "compact-button"
                }
                type="button"
                disabled={busyId === organization.organization_id}
                onClick={() => void toggleStatus(organization)}
              >
                {busyId === organization.organization_id
                  ? "Guardando…"
                  : organization.status === "active"
                    ? "Desactivar"
                    : "Activar"}
              </button>
            </div>

            <dl className="customer-meta">
              <div>
                <dt>RUC</dt>
                <dd>{organization.tax_id ?? "—"}</dd>
              </div>
              <div>
                <dt>Usuarios</dt>
                <dd>{Number(organization.member_count)}</dd>
              </div>
              <div>
                <dt>Clientes</dt>
                <dd>{Number(organization.customer_count)}</dd>
              </div>
              <div>
                <dt>Módulos activos</dt>
                <dd>{Number(organization.enabled_module_count)} / 8</dd>
              </div>
            </dl>
          </article>
        ))}
      </section>
    </div>
  );
}
